import React, { useEffect } from 'react';
import {
  Avatar,
  Box,
  Button,
  Card,
  CardActionArea,
  CardActions,
  CardContent,
  CardMedia,
  Chip,
  Container,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import KeyIcon from '@mui/icons-material/Key';
import CodeIcon from '@mui/icons-material/Code';
import ScatterPlotIcon from '@mui/icons-material/ScatterPlot';
import MarkdownIcon from '@mui/icons-material/ArticleOutlined';
import {
  useAppDispatch,
} from '../../reduxStore/utils/reduxHooks';
//
import ontologyImg from '../../images/home/ontologyGraph.png';
//
// scss
import './Home.scss';
import homeAct from './homeAction';
import services from '../../app/config/services';
import { useNavigate } from 'react-router-dom';
import { routes } from '../../config/config.routeName';
//
import markdownImg from '../../images/markdownIcon/markdown-svgrepo-com.svg';

type CardItem = {
  title: string;
  desc: string;
  img?: string;
  icon?: React.ReactNode;
  badge: string;
  path: string;
};

const dashboardChipSx = {
  color: '#1E3A8A',
  borderColor: 'rgba(37, 99, 235, 0.45)',
  backgroundColor: 'rgba(239, 246, 255, 0.88)',
  fontWeight: 700,
  '& .MuiChip-label': {
    color: '#1E3A8A',
  },
};

const quickChipSx = {
  color: '#0F172A',
  backgroundColor: '#E2E8F0',
  border: '1px solid #CBD5E1',
  fontWeight: 700,
  '& .MuiChip-label': {
    color: '#0F172A',
  },
  '& .MuiChip-icon': {
    color: '#475569',
  },
};

const badgeChipSx = {
  mb: 1.2,
  bgcolor: '#E2E8F0',
  color: '#0F172A',
  borderColor: '#CBD5E1',
  fontWeight: 700,
  '& .MuiChip-label': {
    color: '#0F172A',
  },
};

const cardItems:CardItem[] = [
  {
    title: 'Ontology',
    desc: 'Explore ontology data with interactive graph visualization and relationship insights.',
    img: ontologyImg,
    badge: 'Knowledge Graph',
    path: routes.ONTOLOGY,
  },
  {
    title: 'Markdown Tool',
    desc: 'Convert plain text into your team-specific Markdown format for Firebase Firestore workflows.',
    img: markdownImg,
    badge: 'Text Utility',
    path: routes.MARKDOWN,
  },
  {
    title: 'SPARQL Viewer',
    desc: 'Inspect long SPARQL updates side-by-side with syntax highlighting and easier diff reading.',
    badge: 'Query Utility',
    icon: <CodeIcon sx={{ fontSize: 64 }} />,
    path: routes.SPARQL,
  },
  {
    title: 'Crypto Tool',
    desc: 'Generate tokens, SHA256, HMAC signatures, JWT, and verify file checksum in one page.',
    badge: 'Security Utility',
    icon: <KeyIcon sx={{ fontSize: 64 }} />,
    path: routes.CRYPTO,
  },
];

function Home(): React.JSX.Element {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch({
      type: homeAct.SET_PAGENAME,
      payload: 'HomePage',
    });
  }, []);

  const handleListItemClick = (card:CardItem) => (): void => {
    navigate(`/${services.getLocale()}${card.path}`);
  };

  return (
    <div className="Home">
      <Container className="homeContainer" maxWidth="lg" sx={{ paddingBottom: 8 }}>
        <Box className="homeHero" mt={4} mb={4.5}>
          <Stack direction="row" spacing={1.5} alignItems="center" mb={1.5}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
              <ScatterPlotIcon />
            </Avatar>
            <Chip label="Workspace Dashboard" variant="outlined" sx={dashboardChipSx} />
          </Stack>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Daoyidh Tools Hub
          </Typography>
          <Typography variant="body1" className="homeSubTitle">
            Choose a tool to start your workflow quickly. Everything below is optimized for daily ontology and data operations.
          </Typography>
          <Stack direction="row" spacing={1} mt={2} useFlexGap flexWrap="wrap">
            <Chip size="small" icon={<ScatterPlotIcon />} label="Ontology" sx={quickChipSx} />
            <Chip size="small" icon={<MarkdownIcon />} label="Markdown" sx={quickChipSx} />
            <Chip size="small" icon={<CodeIcon />} label="SPARQL" sx={quickChipSx} />
            <Chip size="small" icon={<KeyIcon />} label="Crypto" sx={quickChipSx} />
          </Stack>
        </Box>

        <Grid container spacing={3}>
          {cardItems.map((card) => (
            <Grid item key={card.title} xs={12} sm={6} md={4}>
              <Card
                key={card.title}
                variant="outlined"
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 3,
                  transition: 'transform 0.18s ease, box-shadow 0.18s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4,
                  },
                }}
              >
                <CardActionArea
                  onClick={handleListItemClick(card)}
                  sx={{
                    display: 'flex', flexDirection: 'column', alignItems: 'stretch', flexGrow: 1,
                  }}
                >
                  {card.img ? (
                    <CardMedia
                      component="img"
                      height="160"
                      image={card.img}
                      alt={card.title}
                      sx={{ objectFit: 'contain', backgroundColor: '#fff' }}
                    />
                  ) : (
                    <Box
                      sx={{
                        height: 160,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'background.default',
                      }}
                    >
                      {card.icon}
                    </Box>
                  )}
                  <CardContent sx={{ width: '100%', flexGrow: 1 }}>
                    <Chip
                      size="small"
                      label={card.badge}
                      variant="outlined"
                      sx={badgeChipSx}
                    />
                    <Typography gutterBottom variant="h5" component="div" sx={{ fontWeight: 600 }}>
                      {card.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ minHeight: 60 }}>
                      {card.desc}
                    </Typography>
                  </CardContent>
                </CardActionArea>
                <CardActions sx={{ px: 2, pb: 2 }}>
                  <Button size="small" onClick={handleListItemClick(card)}>
                    Open Tool
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </div>
  );
}

export default Home;
